import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GovIssue, GovItemDetail, GovSettingsOn } from "../../../../utils/icGovernance";
import { useGovernance } from "../GovernanceLayout";
import {
  ENVIRONMENT_LABEL,
  StatusChip,
  clientLabel,
  dateOnly,
  itemUrl,
  severityLabel,
  statusLabel,
  vendorLabel,
} from "../governanceStatus";
import NewIssueDialog from "../dialogs/NewIssueDialog";
import { NewButton } from "../dialogs/dialogParts";
import { IdLink, LongText, PartyChip, PlainText, TitleLink, localDay } from "./cells";
import RegisterTable, { RegisterColumn, WaitingNotice } from "./RegisterTable";
import { useRegisterItems } from "./useRegisterItems";

// Issue and Bug Log — columns in the client's workbook order.
const issueColumns = (s: GovSettingsOn): RegisterColumn<GovIssue>[] => [
  { key: "id", header: "Issue ID", render: (r) => <IdLink item={r} /> },
  { key: "reportedAt", header: "Date Reported", render: (r) => localDay(r.reportedAt || r.createdAt) },
  { key: "reportedBy", header: "Reported By", render: (r) => <PlainText value={r.createdBy} /> },
  {
    key: "environment",
    header: "Environment",
    render: (r) => <PlainText value={r.environment ? ENVIRONMENT_LABEL[r.environment] ?? r.environment : null} />,
  },
  { key: "severity", header: "Severity", render: (r) => <PlainText value={r.severity ? severityLabel(r.severity) : null} /> },
  { key: "title", header: "Issue Summary", wrap: true, minWidth: 240, render: (r) => <TitleLink item={r} /> },
  {
    key: "steps",
    header: "Steps to Reproduce",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.stepsToReproduce} />,
  },
  {
    key: "expected",
    header: "Expected Result",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.expectedResult} />,
  },
  {
    key: "actual",
    header: "Actual Result",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.actualResult} />,
  },
  { key: "impact", header: "Business Impact", wrap: true, render: (r) => <LongText value={r.businessImpact} /> },
  { key: "leadParty", header: "Lead Party", render: (r) => <PartyChip party={r.leadParty} settings={s} /> },
  { key: "owner", header: `${vendorLabel(s)} Owner`, render: (r) => <PlainText value={r.vendorOwner || r.assignedTo} /> },
  { key: "dueDate", header: "Date Due", render: (r) => dateOnly(r.dueDate) },
  { key: "status", header: "Status", render: (r) => <StatusChip kind="ISSUE" status={r.status} /> },
  { key: "resolution", header: "Fix / Resolution", wrap: true, render: (r) => <LongText value={r.resolution} /> },
  { key: "retestBy", header: "Retest By", render: (r) => <PlainText value={r.retest?.by} /> },
  {
    key: "retestResult",
    header: "Retest Result",
    render: (r) => <PlainText value={r.retest?.result ? statusLabel(r.retest.result) : null} />,
  },
  { key: "closedAt", header: `${clientLabel(s)} Closure Date`, render: (r) => localDay(r.closedAt) },
];

const WAITING: WaitingNotice<GovIssue> = {
  reason: (r) => (r.status === "RETEST" ? "retest" : null),
  describe: (n) => `${n} ${n === 1 ? "issue is" : "issues are"} ready for you to retest and close`,
};

const IssueRegister = () => {
  const { settings } = useGovernance();
  const navigate = useNavigate();
  const register = useRegisterItems<GovIssue>("ISSUE");
  const columns = useMemo(() => issueColumns(settings), [settings]);
  const [newOpen, setNewOpen] = useState(false);

  const onCreated = (item: GovItemDetail) => {
    setNewOpen(false);
    navigate(itemUrl(item));
  };

  return (
    <>
      <RegisterTable
        {...register}
        kind="ISSUE"
        columns={columns}
        noun={{ one: "issue", many: "issues" }}
        caption={`${vendorLabel(settings)} fixes; ${clientLabel(settings)} retests and closes`}
        actions={<NewButton label="New issue" onClick={() => setNewOpen(true)} />}
        waiting={WAITING}
      />
      <NewIssueDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={onCreated} />
    </>
  );
};

export default IssueRegister;
